import { ok, type Result, err } from "./result-pattern";
import { InternalError, ValidationError } from "@/domain/errors/common";

import { APP_CONFIG } from "@/config/app-config";
import { clsx, type ClassValue } from "clsx";
import { ExternalToast } from "sonner";
import { twMerge } from "tailwind-merge";
import { type ZodTypeAny, z } from "zod";
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function handleIDS(id: string, isDesktop: boolean) {
  return isDesktop ? id : `mobile-${id}`;
}

export function parseFromLocalStorage(data: string | null) {
  if (!data) return null;
  return data.includes('"') ? JSON.parse(data) : data;
}

// Utility function to parse data from API based on Zod schema
export const generateDTOWithZod = <TSchema extends ZodTypeAny>(
  schema: TSchema,
  rawData: unknown,
  errorMessage: string
): z.output<TSchema> => {
  const { success, data, error } = schema.safeParse(rawData);

  if (!success) {
    const environment = APP_CONFIG.getCurrentEnvironment();
    if (environment === "development") {
      console.error(errorMessage, error);
    }
    throw new InternalError(errorMessage);
  }

  return data;
};

// Utility function to parse data from API based on Zod schema (Result type)
export const generateResultWithZod = <TSchema extends ZodTypeAny>(
  schema: TSchema,
  rawData: unknown,
  errorMessage: string
): Result<z.output<TSchema>, ValidationError> => {
  const { success, data, error } = schema.safeParse(rawData);

  if (!success) {
    const environment = APP_CONFIG.getCurrentEnvironment();
    if (environment === "development") {
      console.error(errorMessage, error);
    }
    return err(new ValidationError(error, { message: errorMessage }));
  }

  return ok(data);
};

// Helper function to check if a string is an ISO date
const isISODate = (str: string): boolean => {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/.test(str);
};

// Utility function to revive dates
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const reviveDates = (obj: unknown): any => {
  if (typeof obj !== "object" || obj === null) return obj;

  if (Array.isArray(obj)) {
    return obj.map(reviveDates);
  }

  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      if (typeof value === "string" && isISODate(value)) {
        acc[key] = new Date(value); // Convert valid ISO date strings to Date objects
      } else if (typeof value === "object") {
        acc[key] = reviveDates(value);
      } else {
        acc[key] = value;
      }
      return acc;
    },
    {} as Record<string, unknown>
  );
};

export const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const toastDefaultConfig = (isMobile: boolean): ExternalToast => ({
  position: isMobile ? "top-center" : "bottom-right",
});

export async function waitForDuration(durationInMs: number): Promise<void> {
  await new Promise((resolve) => {
    window.setTimeout(resolve, durationInMs);
  });
}
